import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  BlocksRowCellSelectAllEvent,
  BlocksRowDragEnd,
  BlocksTableResizeEvent,
  BlocksTableScrollXEvent,
  BlocksTableScrollYEndEvent,
  BlocksTableScrollYEvent
} from '../../events';
import { BlocksDraggableCellMouseDownEvent } from '../../events/blocks-draggable-cell-mousedown';
import { BlocksKeyboardDragEndEvent } from '../../events/blocks-keyboard-drag-end';
import { BlocksKeyboardDragStartEvent } from '../../events/blocks-keyboard-drag-start';
import { BlocksRowExpandEvent } from '../../events/blocks-row-expand';
import { BlocksTableCellExpandEvent } from '../../events/blocks-table-cell-expand';
import { BlocksScrollXEndEvent } from '../../events/blocks-table-scroll-x-end';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../contextual-menu/contextual-menu';
import '../icon/icons/close';
import '../icon/icons/delta-down';
import '../modal/modal';
import { BlocksModal } from '../modal/modal';
import { BlocksTableCell } from '../table-cell/table-cell';
import { BlocksTableHeaderCell } from '../table-header-cell/table-header-cell';
import { BlocksTableRow } from '../table-row/table-row';
import '../tooltip/tooltip';
import { BlocksContextualMenu } from './../contextual-menu/contextual-menu';
import type { TableCellSize, TableStripeOrder, TooltipAlignment } from './table-object.model';
import styles from './table-object.styles';

interface WidthHeaderCellInfo {
  index: number;
  width: string;
}
interface FrozenHeaderCellInfo {
  index: number;
  offset: number;
}

interface AssociatedCellsInfo {
  initialRow: BlocksTableRow | null;
  foldingRow: BlocksTableRow;
  el: BlocksTableCell;
}

interface FoldableHeaderCellInfo {
  showWidth: number;
  el: BlocksTableHeaderCell;
  initialRow: BlocksTableRow;
  associatedCells: Array<AssociatedCellsInfo>;
}

/**
 * @slot default - The contained element, usually a table or `cat-table`
 * @slot header - Content to appear above the table
 * @slot footer - Content to appear below the table
 * @slot bulkActionSingle - Content to appear as bulkaction content when bulkActionCount is 1 (when a specific bulkActionContent to be shown for a single selected row)
 * @slot bulkActionSingleMore - Content to appear as bulkaction content when bulkActionCount is 1 (when a specific bulkActionContent to be shown for a single selected row with more dropdown)
 * @slot bulkAction - Content to appear as bulkaction content when bulkActionCount is > 1
 * @slot bulkActionMore - Content to appear as bulkaction content when bulkActionCount is > 1 with more dropdown
 * @fires bl-column-sort - emitted when behavior is filter (when wither ascending/descending is selected)
 * @fires bl-table-scroll-y - emitted when table body is scrolled vertically
 * @fires bl-table-scroll-y-end - emitted when table vertical scroll reached bottom-end
 * @fires bl-table-scroll-x - emitted when table body is scrolled horizontally.
 * @fires bl-table-scroll-x-end - emitted when table vertical scroll reached right-end
 * @fires bl-table-resize - emitted on window resize
 * @fires bl-row-expand - emitted when nested component/ accordion in self-foldable table is opened.
 * @fires bl-row-drag-end - emitted when a row is dropped (after dragging and leaving the mouse)
 * @fires bl-row-cell-select-all - emitted when a bulk action is toggled
 * @csspart base - The component's base wrapper.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart part-header - The component's header slot.
 * @csspart tooltip - The component's tooltip element.
 * @csspart body - The component's body wrapper.
 * @csspart bulk-action - The component's bulk-action element.
 * @csspart bulk-action-title - The component's bulk-action title.
 * @csspart bulk-action-buttons - The component's bulk-action buttons.
 * @csspart part-bulkActionSingle - The component's bulkActionSingle slot.
 * @csspart part-bulkAction - The component's bulkAction slot.
 * @csspart bulk-action-more - The component's bulk-action-more element.
 * @csspart more-trigger - The component's more-trigger button.
 * @csspart icon-delta-down - The component's delta-down icon.
 * @csspart part-bulkActionSingleMore - The component's bulkActionSingleMore slot.
 * @csspart part-bulkActionMore - The component's bulkActionMore slot.
 * @csspart bulk-action-close - The component's bulk-action-close button.
 * @csspart icon-close - The component's close icon.
 * @csspart body-inner - The component's body-inner wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 * @csspart modal - The component's modal element.
 * @csspart modal-heading - The component's modal-heading wrapper.
 * @csspart modal-body - The component's modal-body wrapper.
 * @csspart cellHeading-item-mobile - The component's cellHeading-item in mobile wrapper.
 * @csspart cellHeading-text - The component's cellHeading text in mobile.
 * @csspart cellHeading-innerText - The component's cellHeading innerText in mobile.
 * @csspart cellHeading-innerHTML - The component's cellHeading innerHTML in mobile.
 * @csspart self-foldable-item - The component's self-foldable cell inside expandable row.
 * @csspart self-foldable-item-text - The component's text of self-foldable cell inside expandable row.
 * @csspart self-foldable-item-innerText - The component's innerText of self-foldable cell inside expandable row.
 * @csspart self-foldable-item-innerHTML - The component's innerHTML of self-foldable cell inside expandable row.
 * @csspart isExpandableNestedChild-row - The component's isExpandableNestedChild row.
 * @csspart isExpandableChild-row - The component's isExpandableChild row.
 * @csspart isExpandableNestedChild-cell - The component's cell inside isExpandableNestedChild row.
 * @csspart isExpandableChild-cell - The component's cell inside isExpandableChild row.
 */
export class BlocksTableObject extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * isStart state
   * - Controls the beginning gradient on the overflow list
   */
  @state()
  private isStart: boolean | undefined;

  /**
   * isEnd state
   * - Controls the ending gradient on the overflow list
   */
  @state()
  private isEnd: boolean | undefined;

  /**
   * Tooltip state
   * - If ture, renders the tooltip component
   */
  @state()
  private tooltip: boolean | undefined;

  /**
   * Show tooltip state
   * - Controls the header cell tooltip display
   */
  @state()
  private showTooltip: boolean | undefined;

  /**
   * Tooltip text state
   * - Controls the header cell tooltip text
   */
  @state()
  private tooltipText: string | undefined;

  /**
   * Tooltip align state
   * - Controls the header cell tooltip alignment
   */
  @state()
  private tooltipAlign: TooltipAlignment;

  /**
   * Tooltip position state
   * - Controls the header cell tooltip position
   */
  @state()
  private tooltipPosition: Array<number> | undefined = [0, 0];

  /**
   * Frozen cells state
   * - Collection of frozen cells
   */
  @state()
  private frozenCells: Array<HTMLElement> | undefined;

  /**
   * Width cells state
   * - Collection of cells with a width
   */
  @state()
  private widthCells: Array<HTMLElement> | undefined;

  /**
   * Folding header cells state
   * - Collection of folding header cells
   */
  @state()
  private foldingHeaderCells: Array<FoldableHeaderCellInfo> | undefined;

  /**
   * Previous scroll value
   * - Set previous scroll value to determine process
   */
  @state()
  private prevScrollVal = 0;

  /**
   * Scroll timeout state
   */
  @state()
  private scrollTimeout: ReturnType<typeof setTimeout> | undefined;

  /**
   * Is scrolling state
   */
  @state()
  private isScrolling = false;

  /**
   * Current Draggable
   */
  @state()
  private dragRow: BlocksTableRow | null | undefined;

  /**
   * Previous Draggable over
   */
  @state()
  private prevDragRowOver: BlocksTableRow | null | undefined;

  /**
   * Current draggable siblings
   */
  @state()
  private drawRowSiblings: Array<BlocksTableRow> | undefined;

  /**
   * Previous drag position
   */
  @state()
  private prevDragPos: number | undefined;

  /**
   * Previous drag scroll position
   */
  @state()
  private preDragScrollPos: number | undefined;

  /**
   * Resize observer state
   */
  @state()
  private tableResizeObserver: ResizeObserver | undefined = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      for (const entry of entries) {
        this.onResizeObserver(entry);
        // Only ever do first entry.
        break;
      }
    });
  });

  /**
   * Dragged text for announcement - accessibility purpose
   */
  @state()
  private draggedText?: string;

  /**
   * Bulk actions active
   * - If is active, then show the bulk actions flyout
   */
  @property({ type: Boolean })
  isBulkActionActive: boolean | undefined;

  /**
   * Bulk actions count
   * - Counts/displays the number of rows selected in the bulk actions flyout
   */
  @property({ type: Number })
  bulkActionCount = 0;

  /**
   * Bulk actions title
   * - The title that displays right after the count.
   * - Defaults to `Actions`
   */
  @property({ type: String })
  bulkActionTitle: string | undefined;

  /**
   * Column width
   * - Adjusts the default width of the table columns
   * - _Defaults to 200px_
   */
  @property({ type: String })
  columnWidth: string | undefined;

  /**
   * Table object height
   * - Adjusts the default height of the table
   * - _Defaults to 400px_
   */
  @property({ type: String })
  height: string | undefined;

  /**
   * Show more contextual menu label text in bulk-action content
   */
  @property({ type: String }) moreDropdownLabel = 'More';
  /**
   * Min width
   * - Adjusts the min-width to the table
   * - _Defaults to 100%_
   */
  @property({ type: String })
  minWidth: string | undefined;

  /**
   * Table object mobile view breakpoint
   * - default to 768, and can add as property for custom width
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Table object table cell height
   * - defaults to 48px min-height
   * - variant `md` renders table-cell with 40px
   * - variant `sm` renders table-cell with 32px
   */
  @property({ type: String })
  tableCellSize: TableCellSize;

  /**
   * Table object stripeOrder property
   * `default` - renders table-cell with no zebra stripes
   * `odd` - renders table-cell in odd order zebra stripes
   * `even` - renders table-cell in even order zebra stripes
   */
  @property({ type: String })
  stripeOrder: TableStripeOrder = 'default';

  /**
   * Query the cat modal
   */
  private get _catModal(): BlocksModal | null | undefined {
    return this.shadowRoot?.querySelector('cat-modal');
  }

  /**
   * Query the cat table body
   */
  private get _catTableBody(): HTMLElement | null {
    return this.querySelector('cat-table-body');
  }

  /**
   * Query the table object body
   */
  private get _catTableObjectBody(): HTMLElement | undefined | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-object__body');
  }

  /**
   * Query the table object body inner
   */
  private get _catTableObjectBodyInner(): HTMLElement | undefined | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-object__body-inner');
  }

  /**
   * Query the cat table body element
   */
  private get _catTableObjectElement(): HTMLElement {
    return this.shadowRoot?.querySelector('.cat-c-table-object')!;
  }

  /**
   * Get the width of the table object
   */
  private get _catTableObjectWidth(): number {
    return this._catTableObjectElement.getBoundingClientRect().width;
  }

  /**
   * Query the cat table element
   */
  private get _catTableElement(): HTMLElement | null | undefined {
    return this.querySelector('cat-table')?.shadowRoot?.querySelector<HTMLElement>('.cat-c-table');
  }

  /**
   * Query the cat table body element
   */
  private get _catTableHeaderElement(): HTMLElement | null | undefined {
    return this.querySelector('cat-table-header')?.shadowRoot?.querySelector('.cat-c-table-header');
  }

  /**
   * Get the width of the table header
   */
  private get _catTableHeaderWidth(): number | undefined {
    if (this._catTableHeaderElement) {
      return this._catTableHeaderElement.getBoundingClientRect().width;
    }
  }

  /**
   * Query all the rows in the cat table
   */
  private get _catTableRows(): Array<BlocksTableRow> {
    return Array.from(this.querySelectorAll<BlocksTableRow>('cat-table-row'));
  }

  /**
   * Query the rows in the cat table body only
   */
  private get _catTableRowsArray() {
    const children = this._catTableBody?.children;

    if (children) {
      return Array.from(children) as BlocksTableRow[];
    }
  }

  /**
   * Query all the header cells in the cat table
   */
  private get _catTableHeaderCells(): Array<BlocksTableHeaderCell> {
    return Array.from(this.querySelectorAll<BlocksTableHeaderCell>('cat-table-header-cell'));
  }

  /**
   * Check if any rows are expandable
   */
  private get _catIsExpandable() {
    return this._catTableRowsArray?.some((el: BlocksTableRow) => el.isExpandable === true);
  }

  /**
   * Check if any rows are expandable nested
   */
  private get _catIsExpandableNested() {
    return this._catTableRowsArray?.some((el: BlocksTableRow) => el.isExpandableNested === true);
  }

  /**
   * Check if any rows are self foldable
   */
  private get _catIsSelfFoldable() {
    return this._catTableRowsArray?.some((el: BlocksTableRow) => el.isSelfFoldable === true);
  }

  /**
   * Check if any cells are frozen
   */
  private get _catIsFrozenCells() {
    return this._catTableHeaderCells.some((el: BlocksTableHeaderCell) => el.isFrozen === true);
  }

  /**
   * Check if any cells are sticky
   */
  private get _catIsStickyCells() {
    return this._catTableHeaderCells.some((el: BlocksTableHeaderCell) => el.isSticky === true);
  }

  /**
   * Check if any rows have a mobile view
   */
  private get _catIsMobileView() {
    return this._catTableRowsArray?.some((el: BlocksTableRow) => el.isMobileView === true);
  }

  /**
   * Check if any Row cells have a loading state
   */
  private get _catIsTableRowsLoading() {
    return this._catTableRows.some((el: BlocksTableRow) => el.isLoading === true);
  }

  /**
   * Check if any customRows created for Self-foldable Tables are present
   */
  private get _customSelfFoldRows() {
    const element = this._catTableBody?.querySelectorAll<BlocksTableRow>('cat-table-row[isCustomRow]');

    if (element) {
      return Array.from(element);
    }
  }

  /**
   * Show bulk actions
   */
  private get _catIsAnyBulkActions() {
    return (
      this.slotNotEmpty('bulkAction') ||
      this.slotNotEmpty('bulkActionSingle') ||
      this.slotNotEmpty('bulkActionMore') ||
      this.slotNotEmpty('bulkActionSingleMore')
    );
  }

  /**
   * Show bulk actions
   */
  private get _catIsAnyBulkActionsMore() {
    return this.slotNotEmpty('bulkActionMore') || this.slotNotEmpty('bulkActionSingleMore');
  }

  /**
   * Show bulk actions single
   */
  private get _catShowBulkActionSingle() {
    return this.slotNotEmpty('bulkActionSingle') && this.bulkActionCount === 1;
  }

  /**
   * Show bulk actions single more
   */
  private get _catShowBulkActionSingleMore() {
    return this.slotNotEmpty('bulkActionSingleMore') && this.bulkActionCount === 1;
  }

  /**
   * Determines if the current view is in mobile mode.
   * @returns {boolean | undefined} - Returns `true` if the current view is in mobile mode,
   * `false` otherwise. Returns `undefined` if the mobile breakpoint or the mobile view
   * status is not defined.
   */
  private get isMobileView(): boolean | undefined {
    return this.mobileBreakPoint > window.innerWidth && this._catIsMobileView;
  }

  private clearSetTimeoutRowProps?: ReturnType<typeof setTimeout>;
  private clearSetTimeoutRow?: ReturnType<typeof setTimeout>;
  private width?: number;
  private clearSetTimeout?: ReturnType<typeof setTimeout>;
  private catTableBodyScrollheight? = 0;
  private targetRowCells?: NodeListOf<BlocksTableCell>;
  private targetButton?: HTMLElement | null;
  private isMobileDragging = false;
  private cellTooltipAttached = false;
  private isTouchMoved = false;
  private currentDragHoveredRow: null | BlocksTableRow = null;
  private accordionState: boolean | undefined;

  /**
   * First updated lifecycle hook
   * 1. When the component is first updated, run these functions
   */
  firstUpdated() {
    this.setTableHeight();
    this.setTableMinWidth();
    this.setTableColumnWidth();
    this.setTableColumnCount();
    this.setTableHeaderCellZindex();
    this.setMobileView();
  }

  /**
   * Connected callback lifecycle
   * 1. Add window resize event listener
   * 2. Calculate and assign shadow to frozen cells if slotChange occurs
   */
  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;
    setTimeout(() => {
      if (!this._catIsTableRowsLoading) {
        this.setCellTooltip();
      }
      this.setCellWidths();
      this.setStickyCells();
      this.tableResizeObserver?.observe(this.renderRoot.children[0]);
    }, 1);

    this.addEventListener('bl-draggable-cell-mousedown', (evt) => {
      this.startDrag(evt);
    });
    window.addEventListener('resize', this.onWindowResize); /* 1 @TODO: use with resize observer */
    this.addEventListener('bl-table-frozen-change', this.handleFrozenChanged);
    this.addEventListener('bl-table-drag-text', this.announceDragPosition as EventListener);

    this.addEventListener('bl-table-cell-change', () => {
      this.handleFrozenChanged();
      this.setTableColumnCount();
      this.setFrozenShadows(this._catTableObjectBodyInner?.scrollLeft ?? 0, 'onChange'); /* 2*/
    });
    // When self-foldable table-rows are initialized, bl-table-row-initialize will be triggered from table-row
    this.addEventListener('bl-table-row-initialize', this.onRowInitialize);

    // When isExpandable is toggled, bl-table-row-prop-change will be triggered from table-row
    this.addEventListener('bl-table-row-prop-change', this.onRowPropsChange);

    this.addEventListener('bl-keyboard-drag-start', this.handleKeyboardDragStart);

    this.addEventListener('bl-keyboard-drag-end', this.handleKeyboardDragEnd);

    this.addEventListener('bl-keyboard-drag-reset', () => {
      this.dragRow?.classList.remove('cat-dragging-row');
      this.dragRow?.removeAttribute('data-keyboard-dragging');
      this.resetHighlightRow();
    });
    this.addEventListener('bl-table-cell-expand', this.tableCellExpandableButtonClick);
    this.addEventListener('bl-table-header-loading', () => {
      if (this.clearSetTimeout) {
        clearTimeout(this.clearSetTimeout);
      }
      this.clearSetTimeout = setTimeout(async () => {
        clearTimeout(this.clearSetTimeout);
        if (!this._catIsTableRowsLoading) {
          this.setCellTooltip();
        }
      }, 400);
    });
  }

  /**
   * Disconnected callback lifecycle
   * 1. Remove window resize event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.onWindowResize); /* 1 */
    document.removeEventListener('mouseup', this.endDrag);
    document.removeEventListener('mousemove', this.moveDrag);
  }

  private onWindowResize = (evt: Event) => {
    this.onScroll(evt);
    this.setTableColumnCount();
    this.setMobileView();
    this.setTableHeaderCellZindex();
  };

  private handleFrozenChanged() {
    this.setFrozenCells();
    this.setTableHeaderCellZindex();
  }

  /**
   * Update draggedText with rowDraggedText from the custom event's detail property
   */
  private announceDragPosition = (event: CustomEvent<{ rowDraggedText: string }>) => {
    this.draggedText = event?.detail?.rowDraggedText;
  };

  /**
   * Called when SelfFoldable is true
   * 1. Remove all the available customTableRow created for self-foldable rows in setSelfFoldable(), so that it gets removed when the parent row is not available.
   * 2. Call startSelfFoldable() after debounce.
   */
  private onRowInitialize() {
    if (this._customSelfFoldRows) {
      this._customSelfFoldRows.forEach((customTableRow) => {
        customTableRow.remove(); /* 1 */
      });
    }
    if (this.clearSetTimeoutRow) {
      clearTimeout(this.clearSetTimeoutRow);
    }
    this.clearSetTimeoutRow = setTimeout(async () => {
      clearTimeout(this.clearSetTimeoutRow);
      this.startSelfFoldable(); /* 2 */
    }, 400);
  }

  /**
   * Called when SelfFoldable is true
   * 1. Make all the available rows isExpandableActive to false, so the expandable arrows change to original position.
   * 2. Call processSelfFoldable function with width greater than allowed width, so that all Headers come out of selfFolded rows.
   * 3. Do the SelfFoldable by creating new rows.
   * 4. If resize Observer is already called, do the processSelfFoldable again for correct column calculation.
   * 5. Call setExpandableRows for expandable icons and trigger click events.
   */
  private startSelfFoldable = () => {
    this._catTableRowsArray?.forEach((tableRows) => {
      tableRows.isExpandableActive = false; /* 1 */
    });
    if (!this._catIsMobileView) {
      this.closest('cat-table-object')?.style.removeProperty('--cat-table-template-column-width');
    }
    if (this.foldingHeaderCells) {
      this.processSelfFoldable(this.foldingHeaderCells[this.foldingHeaderCells.length - 1].showWidth + 10); /* 2 */
    }
    this.setSelfFoldable(); /* 3 */
    if (this.width) {
      this.processSelfFoldable(this.width); /* 4 */
    }
    this.setExpandableRows(); /* 5 */
  };

  /**
   * Called when `isExpandable` props is toggled dynamically in table-row
   * 1. Call setExpandableRows() only when no rows are self-foldable, since it is already being called in startSelfFoldable().
   * Useful when self-foldable with expandable table variant is created.
   */
  private onRowPropsChange(event: CustomEvent) {
    if (this.clearSetTimeoutRowProps) {
      clearTimeout(this.clearSetTimeoutRowProps);
    }

    if (event.detail.propName === 'isExpandable') {
      this.setTableRole();
    }

    this.clearSetTimeoutRowProps = setTimeout(() => {
      clearTimeout(this.clearSetTimeoutRowProps);
      if (event.detail.propName === 'isExpandable' && !this._catIsSelfFoldable) {
        /* 1 */
        this.setExpandableRows();
      }
    }, 400);
  }
  /**
   * On table body inner scroll
   * 1. Calculate the scroll position(left/top) based on the event target. On window resize, the position is calculated by accessing the bodyInner element
   * Since on window resize, there will be no scroll event, thus we cannot access the target element.
   * 2. On scroll, if position from left is greater than 0, set isStart to false. Otherwise set isStart to true.
   * 3. On scroll, If last child is fully in the viewport, set isEnd to true. Otherwise, set isEnd to false.
   * 4. Fire event emitters on scroll.
   */
  private onScroll = (e: Event) => {
    const scrollEl: Element = e.target as Element;
    /* 1 */
    const scrollTop: number = e.type === 'resize' ? (this._catTableObjectBodyInner?.scrollTop ?? 0) : scrollEl.scrollTop;
    const scrollLeft: number = e.type === 'resize' ? (this._catTableObjectBodyInner?.scrollLeft ?? 0) : scrollEl.scrollLeft;
    /* 2 */
    this.setOverflowShadows();
    this.setFrozenShadows(scrollLeft);

    // Update previous scroll
    this.prevScrollVal = scrollLeft;

    // Do once while scrolling
    if (!this.isScrolling) {
      this.isScrolling = true;
      // Remove tooltip
      this.showTooltip = false;
      // Remove active filter
      if (this._catTableHeaderCells) {
        this._catTableHeaderCells.forEach((el: BlocksTableHeaderCell) => {
          el.isActiveFilter = false;
        });
      }
      // Remove active contextual menu
      if (this.querySelectorAll('cat-contextual-menu').length > 0) {
        Array.from(this.querySelectorAll('cat-contextual-menu'))
          .filter((el: BlocksContextualMenu) => el.isActive)
          .forEach((el: BlocksContextualMenu) => {
            el.isActive = false;
            el.hidePopup();
          });
      }
    }

    // Throttle is scrolling.
    if (this.scrollTimeout) {
      clearInterval(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 100);

    /* 3 */
    // Fire an event emitter when scrolling vertically
    if (scrollTop > 0) {
      this.eventEmit('bl-table-scroll-y', scrollTop) as BlocksTableScrollYEvent;
      //https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#determine_if_an_element_has_been_totally_scrolled
      if (this._catTableObjectBodyInner) {
        const yScrollAtEnd =
          Math.abs(
            this._catTableObjectBodyInner.scrollHeight - this._catTableObjectBodyInner.clientHeight - this._catTableObjectBodyInner.scrollTop
          ) < 1;

        // Fire an event emitter when scrolled to then end of the table vertically
        if (yScrollAtEnd) {
          this.eventEmit('bl-table-scroll-y-end', 'true') as BlocksTableScrollYEndEvent;
        }
      }
    }

    // Fire an event emitter when scrolling horizontally
    if (scrollLeft > 0) {
      this.eventEmit('bl-table-scroll-x', scrollLeft) as BlocksTableScrollXEvent;
      // Fire an event emitter when scrolled to then end of the table horizontally

      if (this._catTableObjectBodyInner) {
        const xScrollAtEnd =
          Math.abs(this._catTableObjectBodyInner.scrollWidth - this._catTableObjectBodyInner.clientWidth - this._catTableObjectBodyInner.scrollLeft) <
          1;
        if (xScrollAtEnd) {
          this.eventEmit('bl-table-scroll-x-end', 'true') as BlocksScrollXEndEvent;
        }
      }
    }
  };

  /**
   * On table object resize
   * 1. Use width to calculate which headers are visible.
   * 2. Adjusts the header cell offset on screen resize.
   * 3. Fire event emitter on window resize.
   */
  private onResizeObserver = (entry: ResizeObserverEntry) => {
    /* 1 */
    const { width } = entry.contentRect;
    this.width = entry.contentRect.width;

    if (this._catIsSelfFoldable) {
      this.processSelfFoldable(width);
    }

    /* 2 */
    if (this._catIsFrozenCells) {
      this.setFrozenCells();
    }

    /* 3 */
    this.eventEmit('bl-table-resize', width) as BlocksTableResizeEvent;
  };

  /**
   * Set table column count and table column key
   * 1. Query all the table header cells in the last table header row.
   * 2. If self foldable and the table object width is smaller than the table header width, then subtract 1 to account for the folding header cell.
   * 3. Count the number of table header cells and set the table columns custom property to the table cell count.
   * 4. For each table header cell, set the key based on the index inside the row.
   * 5. Generate grid template column based on the header custom width.
   * 6. Loop over all the rows to assign style gridTemplateColumn.
   */
  private setTableColumnCount = () => {
    /* 1 */
    const tableHeaderCells = this.querySelector<HTMLElement>('cat-table-header cat-table-row:last-child')?.querySelectorAll('cat-table-header-cell');
    let tableCellCount = 0;

    /* 2 */
    if (tableHeaderCells) {
      if (this._catIsSelfFoldable && this._catTableHeaderWidth !== undefined && this._catTableObjectWidth < this._catTableHeaderWidth) {
        tableCellCount = tableHeaderCells.length - 1; /* 3 */
      } else {
        tableCellCount = tableHeaderCells.length; /* 3 */
      }
    }

    const tableObjectStyle = this.closest('cat-table-object')?.style;
    const tableHeaderRows = this.querySelector('cat-table-header')?.querySelectorAll('cat-table-row');
    if (tableHeaderRows) {
      let templateColumnsRow: string = '';
      tableHeaderRows.forEach((row: BlocksTableRow) => {
        let templateColumns = '';
        row.querySelectorAll<BlocksTableHeaderCell>('cat-table-header-cell').forEach((cell: BlocksTableHeaderCell, index: number) => {
          cell.dataKey = index; /* 4 */
          /* 5 */
          const cellWidth = cell.width;
          if (cellWidth) {
            if (cellWidth.endsWith('%')) {
              const width = (parseInt(cellWidth) / 100) * this._catTableObjectElement.clientWidth + 'px';

              const innerCell = cell.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-header-cell');
              if (innerCell) {
                innerCell.style.width = width;
                innerCell.style.minWidth = width;
              }
              templateColumns = templateColumns + ' ' + width;
            } else {
              templateColumns = templateColumns + ' ' + cellWidth;
            }
          } else if (cell.colspan) {
            templateColumns = templateColumns + ' ' + `${cell.colspan}` + 'fr';
          } else {
            templateColumns = templateColumns + ' ' + '1fr';
          }
        });
        const element = row.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-row');
        if (this.isMobileView) {
          setTimeout(() => {
            if (element) {
              element.style.gridTemplateColumns = '1fr';
            }
          }, 0);
        } else {
          setTimeout(() => {
            if (element) {
              element.style.gridTemplateColumns = templateColumns;
            }
          }, 0);
        }
        templateColumnsRow = templateColumns;
      });

      if (this.isMobileView) {
        tableObjectStyle?.setProperty('--cat-table-template-column-width', '1fr');
        tableObjectStyle?.setProperty('--cat-table-header-column-display', 'none');
        tableObjectStyle?.setProperty('--cat-table-header-column-border-width', '0');
      } else {
        tableObjectStyle?.setProperty('--cat-table-template-column-width', templateColumnsRow);
        tableObjectStyle?.setProperty('--cat-table-header-column-display', 'flex');
        tableObjectStyle?.setProperty('--cat-table-header-column-border-width', '1px');
      }
      /* 6 */
    }

    if (this.isMobileView) {
      tableObjectStyle?.setProperty('--cat-table-column-count', '1');
    } else {
      tableObjectStyle?.setProperty('--cat-table-column-count', tableCellCount.toString());
    }

    /* 3 */
  };

  /**
   * Set table column width
   * 1. If a columnWidth is defined, set the table column width custom property. Defaults to 200px.
   */
  private setTableColumnWidth = () => {
    /* 1 */
    if (this._catTableHeaderCells.some((el: BlocksTableHeaderCell) => el.width)) {
      this.closest('cat-table-object')?.style.setProperty('--cat-table-column-min-width', 'unset');
      this.closest('cat-table-object')?.style.setProperty('--cat-table-column-max-width', 'none');
    } else if (this.columnWidth) {
      this.closest('cat-table-object')?.style.setProperty('--cat-table-column-min-width', this.columnWidth.toString());
    }
  };

  /**
   * Set table height
   * 1. If a tableHeight is defined, set the height custom property. Defaults to 400px.
   */
  private setTableHeight = () => {
    /* 1 */
    if (this.height) {
      return this.closest('cat-table-object')?.style.setProperty('--cat-table-height', this.height.toString());
    }
  };

  /**
   * Set table min width
   * 1. If a minWidth is defined, set the min-width custom property. Defaults to 100%.
   */
  private setTableMinWidth = () => {
    /* 1 */
    if (this.minWidth) {
      return this.closest('cat-table-object')?.style.setProperty('--cat-table-min-width', this.minWidth.toString());
    }
  };

  /**
   * Start draggable
   * 1. Set draggable vars to begin sequence
   * 2. Add draggable events to document (mouse up / mouse move) and sibling elements to determine order
   * 3. Set draggable styles so it is removed from flow
   * 4. Checks if the Accordion is in expanded state. If true, set accordionState to 'true' and collapse it before dragging. If false, set accordionState to 'false'.
   */
  private startDrag = (evt: BlocksDraggableCellMouseDownEvent) => {
    this.dragRow = (evt.composedPath()[0] as HTMLElement).closest('cat-table-row');

    if (!this.dragRow?.isDraggable) {
      return false;
    }

    /* 4 */
    if (this.dragRow?.isExpandable && this.dragRow.isExpandableActive) {
      this.accordionState = true;
      this.tableCellExpandableButtonClick(evt);
    } else {
      this.accordionState = false;
    }

    this.isMobileDragging = true;

    /* 1 */
    const children = this.dragRow.parentElement?.children;

    if (children) {
      this.drawRowSiblings = (Array.from(children) as Array<BlocksTableRow>).filter(
        (row: BlocksTableRow) => row.isDraggable === true && row !== this.dragRow
      );
    }

    this.prevDragPos = evt.detail.event instanceof MouseEvent ? evt.detail.event.clientY : evt.detail.event.targetTouches[0].clientY;
    this.preDragScrollPos = this._catTableObjectBodyInner?.scrollTop;
    this.catTableBodyScrollheight = this._catTableObjectBodyInner?.scrollHeight;

    /* 2 */
    document.addEventListener('mouseup', this.endDrag);
    document.addEventListener('mousemove', this.moveDrag);
    document.addEventListener('touchend', this.endDrag);
    document.addEventListener('touchmove', this.touchMoveDrag);
    this.drawRowSiblings?.forEach((row: BlocksTableRow) => {
      row.addEventListener('mouseover', this.rowOver);
    });

    /* 3 */
    this.dragRow.classList.add('cat-dragging-row');
    if (window.TouchEvent && evt.detail.event instanceof TouchEvent) {
      this.highlightRow(this.dragRow);
    }
  };

  /**
   * End draggable
   * 1. Trigger custom event
   * 2. Clear drag style
   */
  private endDrag = (e: MouseEvent | TouchEvent) => {
    this.isMobileDragging = false;
    if (window.TouchEvent && e instanceof TouchEvent && !this.isTouchMoved) {
      this.clearDragStyle();
      return false;
    }

    if (!this.dragRow || !this.prevDragRowOver) {
      return false;
    }

    /* 1 */
    const draggedRowInfo = {
      rowKey: this.dragRow.dataKey,
      insert: `${this.prevDragRowOver.dragHover}`,
      insertRowKey: this.prevDragRowOver.dataKey,
      expandedState: this.accordionState
    };
    this.dispatch({
      e,
      eventName: 'bl-row-drag-end',
      detailObj: draggedRowInfo
    }) as BlocksRowDragEnd;

    this.clearDragStyle();
    this.isTouchMoved = false;
  };

  /**
   * Clear Drag style
   */
  private clearDragStyle = () => {
    if (this.prevDragRowOver) this.prevDragRowOver.dragHover = undefined;
    this.dragRow?.classList.remove('cat-dragging-row');
    this.dragRow = null;
    document.removeEventListener('mouseup', this.endDrag);
    document.removeEventListener('mousemove', this.moveDrag);
    this.drawRowSiblings?.forEach((row: BlocksTableRow) => {
      row.removeEventListener('mouseover', this.rowOver);
      row.setAttribute('style', '');
    });
    this.resetHighlightRow();
  };
  /**
   * Move draggable

   */
  private moveDrag = (e: MouseEvent) => {
    this.handleDragOnScroll(e.clientY);
  };

  /**
   * Touch draggable
   * 1. Finds the current destination element using touch coordinates
   */
  private touchMoveDrag = (e: TouchEvent) => {
    if (this.isMobileDragging) {
      this.isTouchMoved = true;

      // resets the initial highlight set on touch start
      this.resetHighlightRow();

      this.handleDragOnScroll(e.changedTouches[0].clientY);
      const { clientX, clientY } = e.touches[0];
      const tableCell = this.shadowRoot?.elementFromPoint(clientX, clientY); /* 1 */
      if (tableCell?.matches('cat-table-cell')) {
        const row = tableCell.closest('cat-table-row');
        if (row) {
          this.updateDragIndex(row, e.changedTouches[0].clientY);
        }
      }
    }
  };

  /**
   * Handles drag on scroll
   * 1. Update drag row position
   * 2. Check if the dragged row is near the edges
   * 3. If the dragged row is near the table-body top-edge, decrease the scrolltop of the table-body by 10
   * 4. If the dragged row is near the table-body bottom-edge and if scroll is not at the end, increase the scrolltop of the table-body by 10
   */
  private handleDragOnScroll = (elementClientY: number) => {
    if (!this.dragRow) {
      return false;
    }

    /* 1 */
    if (this.prevDragPos !== undefined) {
      const pos = this.prevDragPos - elementClientY;

      const scrollPos = this._catTableObjectBodyInner?.scrollTop;

      if (scrollPos !== undefined && this.preDragScrollPos !== undefined) {
        this.dragRow.style.marginTop = scrollPos - this.preDragScrollPos - pos + 'px';
      }
    }

    if (this.prevDragRowOver) {
      this.updateDragIndex(this.prevDragRowOver, elementClientY); /* 1 */
    }

    if (this._catTableObjectBodyInner) {
      const distanceTop = this.dragRow.getBoundingClientRect().top - this._catTableObjectBodyInner.getBoundingClientRect().top;
      const distanceBottom = this._catTableObjectBodyInner.getBoundingClientRect().bottom - this.dragRow.getBoundingClientRect().bottom;
      const isNearTop = distanceTop < 50;
      const isNearBottom = distanceBottom < 50; /* 2 */
      if (isNearTop) {
        this._catTableObjectBodyInner.scrollTop -= 10; /* 3 */
      }

      if (
        isNearBottom &&
        this.catTableBodyScrollheight !== undefined &&
        this.catTableBodyScrollheight > this._catTableObjectBodyInner.scrollTop + this._catTableObjectBodyInner.clientHeight
      ) {
        this._catTableObjectBodyInner.scrollTop += 10; /* 4 */
      }
    }
  };

  /**
   * Row over
   */
  private rowOver = (e: MouseEvent) => {
    const row = e.currentTarget as BlocksTableRow;
    const pos = e.clientY;
    this.updateDragIndex(row, pos);
  };

  /**
   * Update drag index
   */
  private updateDragIndex = (row: BlocksTableRow, pos: number) => {
    const boundingClientRect = row.shadowRoot?.children[0].getBoundingClientRect()!;
    const { y, height } = boundingClientRect;
    const dragHover = y + height / 2 > pos ? 'before' : 'after';
    row.dragHover = dragHover;
    if (row !== this.prevDragRowOver) {
      if (this.prevDragRowOver) this.prevDragRowOver.dragHover = undefined;
      this.prevDragRowOver = row;
    }
  };

  private handleKeyboardDragStart = (event: BlocksKeyboardDragStartEvent) => {
    this.dragRow = event.detail?.dragRow;
    if (this.dragRow) {
      this.dragRow.classList.add('cat-dragging-row');
      this.dragRow.setAttribute('data-keyboard-dragging', 'true');
      this.highlightRow(this.dragRow);
    }
  };

  private handleKeyboardDragEnd = (event: BlocksKeyboardDragEndEvent) => {
    const row = event.detail?.row;
    const direction = event.detail?.direction;
    row.dragHover = direction;

    if (row !== this.prevDragRowOver) {
      if (this.prevDragRowOver) this.prevDragRowOver.dragHover = undefined;
      this.prevDragRowOver = row;
    }

    this.dispatch({
      e: event,
      eventName: 'bl-row-drag-end',
      detailObj: {
        rowKey: this.dragRow?.dataKey,
        insert: `${this.prevDragRowOver?.dragHover}`,
        insertRowKey: this.prevDragRowOver?.dataKey
      }
    }) as BlocksRowDragEnd;

    this.clearDragStyle();
  };

  /**
   * Highlights the row for keyboard and mobile drag and drop.
   * @param row
   */
  private highlightRow = (row: BlocksTableRow) => {
    if (this._catTableRowsArray && this._catTableRowsArray?.length > 2) {
      let currentIndex = this._catTableRowsArray?.indexOf(row);
      const lastIndex = this._catTableRowsArray?.length - 1;
      let nextRow;
      if (currentIndex === lastIndex) {
        nextRow = row.previousElementSibling as BlocksTableRow;
      } else {
        nextRow = row.nextElementSibling as BlocksTableRow;
        nextRow.dragHover = 'before';
      }
      this.currentDragHoveredRow = nextRow;
    }
  };

  /**
   * Highlights the row for keyboard and mobile drag and drop.
   * @param row
   */
  private resetHighlightRow = () => {
    if (this.currentDragHoveredRow) {
      this.currentDragHoveredRow.dragHover = undefined;
    }
  };

  /**
   * Sets the frozen shadows.
   * 1. If scrolled position is greater than zero, then add shadows
   * 2. If scroll position is at zero, then remove shadows
   */
  private setFrozenShadows(scrollLeft: number, trigger?: string) {
    if (this.frozenCells && scrollLeft > 0 && (!this.prevScrollVal || trigger === 'onChange')) {
      /* 1 */
      this.frozenCells.forEach((el) => {
        (el as BlocksTableCell).isActiveShadow = true;
      });
    } else if (this.frozenCells && scrollLeft === 0 && this.prevScrollVal > 0) {
      /* 2 */
      this.frozenCells.forEach((el) => {
        (el as BlocksTableCell).isActiveShadow = false;
      });
    }
  }

  /**
   * Set right and left gradients on tables
   * 1. Target the actual table inside table object body
   * 2. Get the width of the table offscreen when table overflows
   * 3. If table width is less than or equal to table object body width, remove all shadows
   * 4. If table object body inner scroll isn't all the way to the left or to the right, turn all shadows on
   * 5. If table body inner scroll is >= to width of table offscreen, turn off right shadow and turn left shadow on
   * 6. Else, set the right shadow to true and the left shadow to false
   */
  private setOverflowShadows() {
    const tableObjectBody = this._catTableObjectBody;
    const tableObjectBodyInner = this._catTableObjectBodyInner;
    if (this._catTableElement && tableObjectBody && tableObjectBodyInner) {
      const tableWidth = this._catTableElement.clientWidth; /* 2 */
      const tableScrollWidth = tableObjectBodyInner.scrollWidth; /* 2 */
      const tableOffScreen = tableScrollWidth - tableWidth; /* 2 */

      if (tableScrollWidth <= tableWidth) {
        /* 3 */
        this.isEnd = false;
        this.isStart = false;
      } else if (tableObjectBodyInner.scrollLeft > 0 && tableObjectBodyInner.scrollLeft < tableOffScreen) {
        /* 4 */
        this.isEnd = true;
        this.isStart = true;
      } else if (tableObjectBodyInner.scrollLeft >= tableOffScreen) {
        /* 5 */
        this.isEnd = false;
        this.isStart = true;
      } else {
        /* 6 */
        this.isEnd = true;
        this.isStart = false;
      }
    }
  }

  /**
   * Sets the frozen table cells from header.
   * 1. Add up all the frozen cell widths in the last header row and set the custom property to adjust the scrollbar horizontal offset.
   * 2. Get the height of the table header and set the custom property to adjust the scrollbar vertical offset.
   * 3. Set the offset for each table header cell.
   * 4. Update the table cells to match the header cell.
   */
  private setFrozenCells = () => {
    const frozenHeaderCells: Array<FrozenHeaderCellInfo> = [];
    // Reset frozen cells.
    this.frozenCells = [];

    /* 1 */
    const lastHeaderRow = this.querySelector<BlocksTableRow>('cat-table-header cat-table-row:last-child');
    let lastHeaderRowFrozenCells = Array.from(lastHeaderRow?.querySelectorAll<BlocksTableHeaderCell>('cat-table-header-cell') ?? []).filter(
      (el) => el.isFrozen === true
    );
    if (lastHeaderRowFrozenCells.length) {
      if (this.mobileBreakPoint > window.innerWidth) {
        lastHeaderRowFrozenCells = [lastHeaderRowFrozenCells[0]];
      }
      const frozenHeaderCellWidths: Array<number> = [];
      lastHeaderRowFrozenCells.forEach((cell) => {
        let cellWidth: number;
        // If the cell has a right border applied, add 1px to account for it.
        const headerCell = cell.shadowRoot?.querySelector('.cat-c-table-header-cell');
        if (headerCell) {
          if (cell.border === 'right') {
            cellWidth = headerCell.clientWidth + 1;
          } else {
            cellWidth = headerCell.clientWidth;
          }

          // Push widths to the array.
          frozenHeaderCellWidths.push(cellWidth);
        }
      });
      // Add up all the values in the array and set the margin left value.
      const marginLeft = frozenHeaderCellWidths.reduce((previousValue: number, el: number) => previousValue + el, 0);
      // Set the scrollbar custom property with the margin left value.
      this._catTableObjectBodyInner?.style.setProperty('--cat-table-scrollbar-margin-left', marginLeft.toString() + 'px');
    }

    /* 2 */
    const frozenHeader = this.querySelector<HTMLElement>('cat-table-header')?.shadowRoot?.querySelector('.cat-c-table-header--frozen');
    if (frozenHeader) {
      // Set timeout to let tooltips apply if present to get proper height.
      setTimeout(() => {
        // Get the height of the frozen header.
        const marginTop = frozenHeader.clientHeight;
        // Set the scrollbar custom property with the margin top value.
        this._catTableObjectBodyInner?.style.setProperty('--cat-table-scrollbar-margin-top', marginTop.toString() + 'px');
      }, 1);
    }

    this._catTableRows.forEach((row: HTMLElement) => {
      const isHeaderRow = row.parentElement?.nodeName === 'CAT-TABLE-HEADER' ? true : false;

      if (isHeaderRow) {
        let cells: Array<BlocksTableHeaderCell>;
        /* 3 */
        const headerRowCells: Array<Element> = isHeaderRow ? Array.from(row.children) : [];
        cells = this._catTableHeaderCells.filter((el: BlocksTableHeaderCell) => el.isFrozen === true);

        if (this.mobileBreakPoint > window.innerWidth) {
          cells = [cells[0]];
        }
        cells.forEach((cell: HTMLElement) => {
          if (headerRowCells.indexOf(cell) !== -1) {
            const index = headerRowCells.indexOf(cell);
            // Get offset.
            const offset = headerRowCells.slice(0, index).reduce((previousValue: number, el: Element) => {
              const headerCell = el.shadowRoot?.querySelector<BlocksTableHeaderCell>('.cat-c-table-header-cell');

              if (headerCell) {
                return previousValue + headerCell.clientWidth;
              } else {
                return previousValue;
              }
            }, 0);
            // Set index.
            frozenHeaderCells.push({
              index,
              offset
            });
            // Update header cell.
            cell.style.setProperty('--cat-table-header-cell-left', offset.toString() + 'px');
            // Add cell to state.
            if (!this.frozenCells?.includes(cell)) {
              this.frozenCells?.push(cell);
            }
          }
        });
      } else {
        /* 4 */
        const cells = Array.from(row.querySelectorAll<BlocksTableCell>('cat-table-cell'));
        if (this.mobileBreakPoint > window.innerWidth) {
          // Reset all the frozen cells before updating them to match with header cell
          cells?.forEach((element: BlocksTableCell) => {
            if (element.isFrozen) {
              element.isFrozen = false;
            }
          });
        }
        frozenHeaderCells.forEach((cellInfo: FrozenHeaderCellInfo) => {
          if (cells[cellInfo.index]) {
            // Update cells to match header cell.
            cells[cellInfo.index].isFrozen = true;
            cells[cellInfo.index].style.setProperty('--cat-table-cell-left', cellInfo.offset.toString() + 'px');
            // Add cell to state.
            if (!this.frozenCells?.includes(cells[cellInfo.index])) {
              this.frozenCells?.push(cells[cellInfo.index]);
            }
          }
        });
      }
    });
  };

  /**
   * Sets the table cell width based on the header cells
   * 1. Set the width for each table header cell.
   * 2. Update the table cells to match the header cell.
   */
  private setCellWidths = () => {
    const widthHeaderCells: Array<WidthHeaderCellInfo> = [];
    // Reset width cells.
    this.widthCells = [];

    this._catTableRows.forEach((row: HTMLElement) => {
      const isHeaderRow = row.parentElement?.nodeName === 'CAT-TABLE-HEADER';
      if (isHeaderRow) {
        /* 1 */
        let cells: Array<BlocksTableHeaderCell>;
        const headerRowCells: Array<Element> = isHeaderRow ? Array.from(row.children) : [];
        cells = this._catTableHeaderCells.filter((el: BlocksTableHeaderCell) => el.width);
        cells.forEach((cell: BlocksTableHeaderCell) => {
          if (headerRowCells.indexOf(cell) !== -1) {
            const index = headerRowCells.indexOf(cell);
            // Get width.
            const width = cell.width?.toString();
            // Set index.
            if (width) {
              widthHeaderCells.push({
                index,
                width
              });
            }

            // Add cell to state.
            if (!this.widthCells?.includes(cell)) {
              this.widthCells?.push(cell);
            }
          }
        });
      } else {
        /* 2 */
        const cells = Array.from(row.querySelectorAll<BlocksTableCell>('cat-table-cell'));
        widthHeaderCells.forEach((cellInfo: WidthHeaderCellInfo) => {
          if (cells[cellInfo.index]) {
            // Add cell to state.
            if (!this.widthCells?.includes(cells[cellInfo.index])) {
              this.widthCells?.push(cells[cellInfo.index]);
            }
          }
        });
      }
    });
  };

  /**
   * Sets the sticky table cells from header.
   * 1. If there are frozen cells, then shift over the sticky cell to not overlap it, else set to 0.
   * 2. Set the custom property to offset the sticky table header cell.
   */
  private setStickyCells = () => {
    if (this._catIsStickyCells) {
      const frozenCells = this._catTableHeaderCells.filter((el: BlocksTableHeaderCell) => el.isFrozen === true);
      const stickyCells = this._catTableHeaderCells.filter((el: BlocksTableHeaderCell) => el.isSticky === true);
      stickyCells.forEach((cell: HTMLElement, index: number) => {
        let offset;
        if (index === 0) {
          /* 1 */
          if (frozenCells.length > 0) {
            const headerCell = cell.shadowRoot?.querySelector<BlocksTableHeaderCell>('.cat-c-table-header-cell');

            if (headerCell) {
              offset = headerCell.getBoundingClientRect().left;
            }
          } else {
            offset = 0;
          }
          /* 2 */
          this.style.setProperty('--cat-table-header-cell-sticky-left', offset?.toString() + 'px');
        }
      });
    }
  };

  /**
   * Sets tooltips for cells
   * 1. Loop through all the table rows and cells
   * 2. Assumes only text in cells
   * 3. Use range and count more than 2 render rectangles
   * 4. If there are more than two rects., then fire the mouse events
   * 5. Check for the overflow and Set the tooltip to true to enable the line clamp
   * 6. If the first cell, then set the isFirst property to true
   */
  private setCellTooltip() {
    /* 1 */
    if (this.cellTooltipAttached) {
      return;
    }
    this._catTableRows.forEach((row: BlocksTableRow) => {
      const rowTableCells = row.querySelectorAll<BlocksTableHeaderCell>('cat-table-header-cell, cat-table-cell');
      rowTableCells.forEach((cell: BlocksTableHeaderCell, index: number) => {
        /* 2 */
        const cellTextWrapper = cell.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-header-cell__title, .cat-c-table-cell__title');
        const nodes: Array<Node> | undefined = cellTextWrapper?.querySelector('slot')?.assignedNodes({ flatten: true });
        if (nodes?.[0] === undefined) {
          return null;
        }
        /* 3 */
        const range: Range = document.createRange();
        range.selectNodeContents(nodes[0]);

        const rects: DOMRectList = range.getClientRects();
        /* 4 */
        if (rects.length > 2 && nodes?.[0].nodeName === '#text') {
          /* 5 */
          cell.tooltip = true;
          cell.updateComplete.then(() => {
            if (cellTextWrapper && cellTextWrapper.offsetHeight < cellTextWrapper.scrollHeight) {
              this.tooltip = true;
              this.tooltipText = cell.innerText;
              cell.addEventListener('mouseover', this.cellOver);
              cell.addEventListener('mouseout', this.cellOut);
            } else {
              cell.tooltip = false;
            }
          });

          /* 6 */
          if (index === 0) {
            cell.setAttribute('first', 'true');
          }
        }
      });
      this.cellTooltipAttached = true;
    });
  }

  /**
   * Table cell mouse over
   * 1. Get the text inside the cell title and set the tooltip text to display inside the tooltip content
   * 2. Only set the tooltip if the header cell filter is not active and there is text
   * 3. Set the tooltip top position based on the table row position
   * 4. If the cell is first, then adjust the tooltip alignment and left position
   * 5. If the cell is frozen then don't reposition the tooltip based on scroll
   * 6. Set the tooltip alignment based on if the cell is first or not
   * 7. Set the tooltip position and to show on hover
   */
  private cellOver = (e: Event): void => {
    const cell: BlocksTableHeaderCell = e.target as BlocksTableHeaderCell;
    let cellElement;
    if (cell.nodeName === 'CAT-TABLE-HEADER-CELL') {
      cellElement = cell.shadowRoot?.querySelector<BlocksTableHeaderCell>('.cat-c-table-header-cell');
    } else if (cell.nodeName === 'CAT-TABLE-CELL') {
      cellElement = cell.shadowRoot?.querySelector<BlocksTableCell>('.cat-c-table-cell');
    }

    /* 1 */
    let cellTitleText;
    if (cellElement && cellElement.querySelector('slot')) {
      cellTitleText = cellElement.querySelector('slot')?.assignedNodes({ flatten: true })[0].textContent;
    }

    /* 2 */
    if ((cell.isActiveFilter === false || !cell.isActiveFilter) && cellTitleText) {
      this.tooltipText = cellTitleText; /* 1 */
      const tableRow = cell.parentElement?.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-row')?.getBoundingClientRect()!;
      const rowTopPosition = tableRow.top + window.scrollY;

      const tooltipArrowHeight = 12;
      const top = rowTopPosition - tooltipArrowHeight; /* 3 */

      /* 4 */
      let left;
      const cellElementBoundingClient = cellElement?.getBoundingClientRect()!;
      if (cell.hasAttribute('first')) {
        /* 5 */
        if (cell.isFrozen) {
          left = cellElementBoundingClient.left + 16;
        } else {
          left = cellElementBoundingClient.left + 16 - this.prevScrollVal;
        }
        // Need to set top align to show the tooltip correctly on mobile/rtl view
        this.tooltipAlign = 'top'; /* 6 */
      } else {
        const clientWidth = cellElement?.clientWidth!;
        // Position of the tooltip will be misaligned when scrolling the table header to left or right if we reduce prevScrollVal from left.
        left = cellElementBoundingClient.left + clientWidth / 2;
        this.tooltipAlign = 'top'; /* 6 */
      }

      /* 7 */
      this.tooltipPosition = [top, left];
      this.showTooltip = true;
    }
  };

  /**
   * Table cell mouse out
   * 1. Hide the tooltip on mouse out of the table cell
   */
  private cellOut = (): void => {
    this.showTooltip = false;
  };

  /**
   * Set expandable for self foldable rows
   * 1. If the table object width is smaller than the table header, then enable expandable rows for self foldable rows.
   * 2. If row is an expandable child, then set expandable nested if has self foldable items.
   * 3. If row is an expandable, then set expandable if has self foldable items.
   */
  private setSelfFoldableExpandable() {
    /* 1 */
    if (this._catTableHeaderWidth !== undefined && this._catTableObjectWidth < this._catTableHeaderWidth) {
      this._catTableRowsArray?.forEach((row: BlocksTableRow) => {
        if (row.isSelfFoldable) {
          if (row.isExpandableChild) {
            /* 2 */
            row.isExpandableNested = true;
          } else {
            /* 3 */
            row.isExpandable = true;
          }
        }
      });
    }
  }

  /**
   * Setup self foldable
   * 1. Add folding cells (do once)
   * 2. Prevent scrolling if any row is self foldable
   * 3. Adding foldable child row for populating clipped columns.
   * 4. Add all folding header cells to state.
   */
  private setSelfFoldable = () => {
    /* 1 */
    if (this._catIsSelfFoldable) {
      this.setSelfFoldableExpandable();

      /* 2 */
      const tableBodyInner = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-table-object__body-inner');

      if (tableBodyInner) {
        tableBodyInner.style.overflowX = 'hidden';
      }

      /* 3 */
      // @TODO Remove the self foldable child row if there are no cells in it
      let prevShowWidth = 0;
      const selfFoldableRows = this._catTableRowsArray?.filter((el: BlocksTableRow) => el.isSelfFoldable === true);
      selfFoldableRows?.forEach((row: BlocksTableRow) => {
        if (row.isExpandableChild) {
          row.insertAdjacentHTML(
            'afterend',
            `<cat-table-row part="isExpandableNestedChild-row" exportparts=${this.getExportParts('table-row-isExpandableNestedChild', 'table-row')}
            isExpandableNestedChild isSelfFoldableChild isCustomRow isEmptySelfFoldable>
              <cat-table-cell part="isExpandableNestedChild-cell" exportparts=${this.getExportParts(
                'table-cell-isExpandableNestedChild',
                'table-cell'
              )} isFirst=${true} styleModifier="cat-c-table-cell__self-foldable"></cat-table-cell>
            </cat-table-row>`
          );
        } else {
          row.insertAdjacentHTML(
            'afterend',
            `<cat-table-row part="isExpandableChild-row" exportparts=${this.getExportParts(
              'table-row-isExpandableChild',
              'table-row'
            )} isExpandableChild isSelfFoldableChild isCustomRow isEmptySelfFoldable>
              <cat-table-cell part="isExpandableChild-cell" exportparts=${this.getExportParts(
                'table-cell-isExpandableChild',
                'table-cell'
              )} isFirst=${true} styleModifier="cat-c-table-cell__self-foldable"></cat-table-cell>
            </cat-table-row>`
          );
        }
      });

      /* 4 */
      this.foldingHeaderCells = Array.from(
        this.querySelectorAll<BlocksTableHeaderCell>('cat-table-header > cat-table-row > cat-table-header-cell')
      ).map((cell, index) => {
        const element = cell.shadowRoot;
        let cellStyles;

        if (element) {
          cellStyles = window.getComputedStyle(element.children[0]);
        }

        const minWidthStyle = cellStyles?.getPropertyValue('min-width');
        const minWidth = minWidthStyle?.includes('px') ? parseInt(minWidthStyle.replace('px', ''), 10) : cell.clientWidth;
        prevShowWidth = prevShowWidth + minWidth;

        return {
          showWidth: prevShowWidth,
          el: cell,
          initialRow: cell.parentElement as BlocksTableRow,
          associatedCells: (Array.from(this.querySelectorAll<HTMLElement>(`cat-table-cell:nth-child(${index + 1})`)) as BlocksTableCell[]).map(
            (el: BlocksTableCell) => {
              const initialRow = el.closest('cat-table-row');
              const foldingRow = initialRow?.nextElementSibling as BlocksTableRow;

              return {
                initialRow,
                foldingRow,
                el
              };
            }
          )
        };
      });
    }
  };

  /**
   * Process self foldable rows
   */
  private processSelfFoldable(width: number) {
    if (this.foldingHeaderCells) {
      // Determine which cells to show.
      this.foldingHeaderCells.forEach((cellInfo, index) => {
        // Don't adjust first item.
        if (index === 0) {
          return null;
        }
        if (width < cellInfo.showWidth) {
          // Abort if already processed.
          if (!cellInfo.el.isConnected) {
            return null;
          }
          // Remove header el.
          cellInfo.el.remove();
          // Fold cells.
          cellInfo.associatedCells.forEach((cell: AssociatedCellsInfo) => {
            if (cell.el.closest('cat-table-row') === cell.initialRow) {
              cell.el.remove();
              if (cell.foldingRow) {
                // Query the closest table cell in the current folding row with a data-index attribute
                let indexEl: BlocksTableCell | HTMLElement | null | undefined = cell.foldingRow
                  .querySelector<BlocksTableCell>(`[data-index]`)
                  ?.closest('cat-table-cell');
                let position: InsertPosition;
                // Check if there is any cell item already folded into the current table cell
                if (indexEl && indexEl.querySelector<HTMLElement>('.cat-c-table-cell__self-foldable-item')) {
                  // If an item already exists, compare its index with the current table header cell index
                  const element = indexEl.querySelector<HTMLElement>('.cat-c-table-cell__self-foldable-item')?.dataset?.index;
                  if (element !== undefined && +element > index) {
                    // If the existing item has a higher index, insert before the existing item
                    position = 'afterbegin';
                  } else {
                    const prevCellItem = cell.foldingRow.querySelector<HTMLElement>(`[data-index="${index - 1}"]`);
                    // Get the index of the cell item before the current index
                    const prevIndex = prevCellItem?.dataset?.index;

                    // Check if there is a cell item with an index less than the current index
                    if (prevIndex !== undefined && +prevIndex < index) {
                      // Update indexEl to the cell with the previous index and set position to insert after it
                      indexEl = prevCellItem;
                      position = 'afterend';
                    } else {
                      // If no item with a lower index is found, insert at the end
                      position = 'beforeend';
                    }
                  }
                } else {
                  // If no existing item is found, insert at the end
                  position = 'beforeend';
                }
                const insertEl = indexEl ? indexEl : cell.foldingRow.children[0];
                // If cell has inner HTML, then print the self foldable cell.
                if (cell.el.innerHTML) {
                  insertEl.insertAdjacentHTML(
                    position,
                    `<div part="self-foldable-item" class="cat-c-table-cell__self-foldable-item" data-index="${index}">
                    <div part="self-foldable-item-text"><strong part="self-foldable-item-innerText">${cellInfo.el.innerText}</strong></div>
                    <div part="self-foldable-item-innerHTML" class="cat-c-table-cell__cell-content"></div>
                  </div>`
                  );
                  /**
                   * As passing innerHTML will remove the event listeners attached to the element, we will query the table-cell chilNodes
                   * which is in light dom(slot) and append it to the above created div (cat-c-table-cell__cell-content) when the cell is moved to self-foldable row.
                   */
                  Array.from(cell.el.childNodes).forEach((child) => {
                    cell.foldingRow.children[0].querySelector(`[data-index="${index}"] div.cat-c-table-cell__cell-content`)?.append(child);
                  });
                  // To display the row
                  if (cell.foldingRow.isEmptySelfFoldable) {
                    cell.foldingRow.isEmptySelfFoldable = false;
                  }
                }
              }
            }
          });
        } else {
          // Abort if already processed.
          if (cellInfo.el.isConnected) {
            return null;
          }
          // Attached header.
          cellInfo.initialRow.appendChild(cellInfo.el);
          cellInfo.associatedCells.forEach((cell: AssociatedCellsInfo) => {
            if (cell.foldingRow) {
              Array.from(cell.foldingRow.querySelectorAll(`[data-index="${index}"]`)).forEach((el) => {
                /**
                 * The table-cells light dom which got appended to the self-folding row above is moved out to their original position inside table-cell when it moves out of self-foldable row.
                 */
                const element = el.querySelector('div.cat-c-table-cell__cell-content')?.childNodes;
                if (element) {
                  Array.from(element)?.forEach((child) => {
                    cell.el.append(child);
                  });
                }

                el.remove();
              });
              // if no cells are self-folded inside the row,hide the row
              if (Array.from(cell.foldingRow.querySelectorAll(`.cat-c-table-cell__self-foldable-item`)).length === 0) {
                cell.foldingRow.isEmptySelfFoldable = true;
              }
            }
            cell.initialRow?.appendChild(cell.el);
          });
        }
      });
      // Adjust column width.
      this.setTableColumnCount();
    }
  }

  /**
   * Set expandable first cells
   * 1. Loop through all the rows.
   * 2. If the row is mot expandable, set isExpandable and isExpandableActive to false.
   * 3. If is the first table cell and is expandable, then set the cell expandable to true to show the accordion arrow
   * 4. If is first table cell and row is expandable nested child, then set the cell is expanded nested child to true.
   * 5. If first table cell and is not expandable, then set isFirst to true to align the cells vertically
   */
  private setExpandableRowsFirstCells() {
    /* 1 */
    this._catTableRows.forEach((row: BlocksTableRow) => {
      /* 2 */
      if (!row.isExpandable) {
        row.isExpandableActive = false;
      }

      const rowTableCells = row.querySelectorAll<BlocksTableCell>('cat-table-header-cell, cat-table-cell');
      rowTableCells.forEach((cell: BlocksTableCell, index: number) => {
        /* 3 */
        if (
          (index === 0 && row.isExpandable) ||
          (index === 0 && row.isExpandableNested) ||
          (index === 0 && cell.nodeName === 'CAT-TABLE-HEADER-CELL')
        ) {
          cell.isExpandable = true;
        }

        /* 4 */
        if (index === 0 && row.isExpandableNestedChild) {
          cell.isExpandableNestedChild = true;
        }

        /* 5 */
        if (index === 0 && !row.isExpandable) {
          cell.isFirst = true;
          if (cell.nodeName !== 'CAT-TABLE-HEADER-CELL' && !row.isExpandableNested) {
            cell.isExpandable = false;
            cell.isExpandableActive = false;
          }
        }
      });
    });
  }

  /**
   * Display the table rows on click of expandable button in table cell
   * 1. Get the parent row of the expandable button
   * 2. Check if the row is Expandable or ExpandableNested
   * 3. Loop and get all the rows which are associated with our expandable row or expandable nested row
      i) Query all the rows which comes after our parent row in the DOM
      ii) Loop till the next row is not expandable or expandable nested row and accumulate in an array
      iii) Eject when the row is expandable or expandable nested row
   */
  private tableCellExpandableButtonClick(event: BlocksTableCellExpandEvent) {
    if (!this._catTableRowsArray) return;
    const el = (event.target as HTMLElement)?.closest('cat-table-row'); /* 1 */
    if (!el) return;
    const parent = el.isExpandable ? 'isExpandable' : 'isExpandableNested'; /* 2 */
    const groups = [
      el,
      ...this._catTableRowsArray.slice(this._catTableRowsArray.indexOf(el) + 1).reduce((acc: BlocksTableRow[], curr: BlocksTableRow, i, arr) => {
        if (
          (el.isExpandable && curr.isExpandable && curr.nextElementSibling) ||
          (el.isExpandableNested && curr.isExpandableNested && curr.nextElementSibling)
        ) {
          // Eject early by mutating iterated copy
          arr.splice(i + 1);
        }

        return [...acc, curr];
      }, [])
    ]; /* 3 */
    if (groups.length === 0) return;
    this.toggleExpandableRows(parent, groups, el);
  }
  /**
   * Set expandable active state
   * 1. Toggle the active state between true and false.
   * 2. Toggle the state of aria-expanded when a parent row is clicked.
   * 3. Toggle the active class to row table cell when clicked.
   * 4. Toggle the active class on expandable children when the parent row is clicked.
   * 5. Custom event emitter for the row expanded
   */
  private toggleExpandableRows(parent: String, group: Array<BlocksTableRow>, el: BlocksTableRow) {
    el.isExpandableActive = !el.isExpandableActive; /* 1 */
    el.isAriaExpanded = el.isAriaExpanded ? false : true; /* 2 */

    /* 3 */
    const tableCells = el.querySelectorAll('cat-table-cell');
    if (tableCells) {
      tableCells.forEach((cell: BlocksTableCell, index: number) => {
        if (index === 0) {
          cell.isExpandableActive = !cell.isExpandableActive;
        }
      });
    }

    /* 4 */
    group.forEach((row: BlocksTableRow) => {
      if (parent === 'isExpandable') {
        row.isExpandableChildActive = row.isExpandableChildActive ? false : true;
      } else {
        row.isExpandableNestedChildActive = row.isExpandableNestedChildActive ? false : true;
      }
    });

    /* 5 */
    let eventValue;
    if (parent === 'isExpandable') {
      eventValue = 'parent-row-' + el.dataKey;
    } else {
      eventValue = 'child-row-' + el.dataKey;
    }
    this.eventEmit('bl-row-expand', eventValue) as BlocksRowExpandEvent;
  }

  /**
   * Set dateKey for both expandable and expandable nested rows
   */
  private setDataKey() {
    /* 1 */
    if (!this._catTableRowsArray) return;

    this._catTableRowsArray
      .filter((el: BlocksTableRow) => el.isExpandable === true)
      .map((el: BlocksTableRow, index: number) => {
        el.dataKey = index.toString();
      });

    this._catTableRowsArray
      .filter((el: BlocksTableRow) => el.isExpandableNested === true)
      .map((el: BlocksTableRow, index: number) => {
        el.dataKey = index.toString();
      });
  }

  /**
   * Set expandable rows
   * 1.) When no rows are Expandable, iterate through rows and get the first cell and change `isFirst` and  `isExpandable` to false.
   */
  private setExpandableRows = () => {
    if (this._catIsExpandable || this._catIsExpandableNested) {
      this.setTableRole();
      this.setExpandableRowsFirstCells();
      this.setDataKey();
    }
    if (!this._catIsExpandable && !this._catIsExpandableNested) {
      const firstHeaderCell = this.querySelector<BlocksTableHeaderCell>('cat-table-header-cell:first-of-type');

      if (firstHeaderCell) {
        firstHeaderCell.isExpandable = false;
      }

      this._catTableRows.forEach((row: BlocksTableRow) => {
        row.isExpandableActive = false;
        const rowTableCells = row.querySelectorAll<BlocksTableCell>('cat-table-cell');
        rowTableCells.forEach((cell: BlocksTableCell, index: number) => {
          /* 1 */
          if (index === 0) {
            cell.isFirst = false;
            cell.isExpandable = false;
            cell.isExpandableActive = false;
          }
        });
      });
    }
  };

  /**
   * Set bulk action active state
   * 1. Toggle the active state between true and false
   */
  private toggleBulkActionActive() {
    this.isBulkActionActive = !this.isBulkActionActive; /* 1 */
    this.dispatch({ eventName: 'bl-row-cell-select-all', detailObj: { isSelected: this.isBulkActionActive } }) as BlocksRowCellSelectAllEvent;
  }

  /**
   * Set table role attribute
   * 1. If any rows are expandable, set the table role to tree grid for accessibility
   */
  private setTableRole() {
    this._catTableElement?.setAttribute('role', 'treegrid'); /* 1 */
  }

  /**
   * Set table header cell z-index
   * 1. Prevent the filter dropdowns from being overlapped by the following row.
   * 2. If the cell is frozen, give it a higher z-index to prevent overlap on scroll
   * 3. Subtract the row index for the default z-index value
   * 4. Apply an incremented z-index custom property to each table header cell
   */
  private setTableHeaderCellZindex = () => {
    /* 1 */
    const tableHeaderRows = this.querySelector('cat-table-header')?.querySelectorAll('cat-table-row');
    if (tableHeaderRows) {
      tableHeaderRows.forEach((row: BlocksTableRow, index: number) => {
        row.querySelectorAll('cat-table-header-cell').forEach((cell: BlocksTableHeaderCell, rowIndex: number) => {
          let indexValue;
          /* 2 */
          if (cell.isFrozen && this.mobileBreakPoint <= window.innerWidth) {
            indexValue = 400 - index; /* 3 */
          } else if (this.mobileBreakPoint > window.innerWidth && rowIndex === 0) {
            const colspan = cell?.colspan;
            if (colspan === undefined || colspan === 1) {
              indexValue = 400 - index; /* 3 */
            } else {
              indexValue = 300 - index; /* 3 */
            }
          } else {
            indexValue = 300 - index; /* 3 */
          }
          cell.style.setProperty('--cat-table-header-cell-zindex', indexValue.toString()); /* 4 */
        });
      });
    }
  };

  /**
   * Set mobile view
   * 1. Toggle the mobile view active state and modal
   * 2. For the target row clicked, create an array of div's with table header and cell content to print in the modal
   * 3. Set the modal heading to use the first table header cell inner text
   * 4. Set the markup in the modal body
   * 5. Return focus to the button on click of the modal back button
   * 6. Return focus to the button on press of the escape key in the modal
   */
  private toggleMobileViewModal = (e: KeyboardEvent | MouseEvent): void => {
    /* 1 */
    if (e.type === 'click' || (e instanceof KeyboardEvent && (e.key === KEYS.ENTER || e.key === KEYS.SPACE))) {
      e.preventDefault();
      if (this._catModal) {
        this._catModal.isActive = !this._catModal.isActive;
      }

      const target = e.currentTarget as HTMLElement;
      this.targetButton = target.shadowRoot?.querySelector<HTMLElement>('.cat-c-button');
      const targetRow = (target.getRootNode() as ShadowRoot).host.closest('cat-table-row') as BlocksTableRow;
      this.targetRowCells = targetRow.querySelectorAll('cat-table-cell');
      let targetModalBody = '';

      /* 2 */
      if (this.targetRowCells) {
        this.targetRowCells.forEach((_, index) => {
          // Skipping all the header cells which are having colspan to get the correct heading for modal
          const cellHeading = this.querySelectorAll<BlocksTableHeaderCell>(`cat-table-header-cell:not([colspan])`)[index];
          targetModalBody += `
            <div part="cellHeading-item-mobile" data-index="${index}">
              <div part="cellHeading-text"><strong part="cellHeading-innerText">${cellHeading?.innerText}</strong></div>
              <div part="cellHeading-innerHTML" class="cat-c-table-cell__cell-content-mobile"></div>
            </div>
          `;
        });
      }

      /* 3 */
      const modalHeading = this._catModal?.querySelector('.cat-c-modal__heading');
      const element = this.querySelector<BlocksTableHeaderCell>(`cat-table-header cat-table-row:last-child cat-table-header-cell:first-child`);

      if (modalHeading && element) {
        modalHeading.innerHTML = element.innerText;
      }

      /* 4 */
      const modalBody = this._catModal?.querySelector('.cat-c-modal__body');

      if (modalBody) {
        modalBody.innerHTML = ''; /* 5 */
      }

      if (targetModalBody && modalBody) {
        setTimeout(() => {
          modalBody.innerHTML = targetModalBody;
          /**
           * As passing innerHTML will remove the event listeners attached to the element, we will query the table-cell chilNodes
           * which is in light dom(slot) and append it to the above created div (cat-c-table-cell__cell-content-mobile) when modal is opened.
           */
          this.targetRowCells?.forEach((cell: BlocksTableCell, index) => {
            Array.from(cell.childNodes).forEach((child) => {
              modalBody.querySelector(`[data-index="${index}"] div.cat-c-table-cell__cell-content-mobile`)?.append(child);
            });
          });
        }, 2);
      }

      /* 5 */
      const modalBackButton: HTMLElement | null | undefined = this._catModal?.shadowRoot?.querySelector('.cat-c-modal__back-button');
      modalBackButton?.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        this.restoreChild();
      });

      /* 6 */
      this._catModal?.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === KEYS.ESCAPE) {
          this.restoreChild();
        }
      });
    }
  };

  /**
   * The table-cells light dom which got appended to the modal-body above is moved out to their original position inside table-cell when modal is closed.
   */
  private restoreChild() {
    const modalBody = this._catModal?.querySelector('.cat-c-modal__body');
    if (this.targetRowCells?.length) {
      Array.from(this.targetRowCells).forEach((cell: BlocksTableCell, index) => {
        const childNodes = modalBody?.querySelector(`[data-index="${index}"] div.cat-c-table-cell__cell-content-mobile`)?.childNodes;

        if (childNodes) {
          Array.from(childNodes)?.forEach((child) => {
            cell.append(child);
          });
        }
      });
    }
    this.targetButton?.focus();
  }

  /**
   * Start mobile view on screen sizes smaller then 560px
   * 1. Add event listener for click or keyboard event that toggles the mobile view modal
   * 2. If row has other functionality, then disable it
   * 3. Set the table column count to 1
   * 4. If is the first table cell, the set the cell mobile view to true
   * 5. Hide all the cells that are not in the first column
   */
  private startMobileView() {
    this._catTableRows.forEach((row: BlocksTableRow) => {
      if (row.isMobileView) {
        setTimeout(() => {
          const trigger: HTMLElement | null | undefined = row
            .querySelector('cat-table-cell')
            ?.shadowRoot?.querySelector('.cat-c-table-cell__mobile-view-button');
          /* 1 */
          if (trigger) {
            trigger.addEventListener('click', this.toggleMobileViewModal);
            trigger.addEventListener('keydown', this.toggleMobileViewModal);
          }
        }, 1);

        /* 2 */
        row.isSelfFoldable = false;
      }

      const tableObjectStyle = this.closest('cat-table-object')?.style;
      tableObjectStyle?.setProperty('--cat-table-column-count', '1'); /* 3 */
      const rowTableCells = row.querySelectorAll<BlocksTableCell>('cat-table-header-cell, cat-table-cell');
      if (rowTableCells) {
        rowTableCells.forEach((cell: BlocksTableCell, index: number) => {
          if ((row.isMobileView && index === 0) || (cell.nodeName === 'CAT-TABLE-HEADER-CELL' && index === 0)) {
            cell.isMobileView = true; /* 4 */
            tableObjectStyle?.setProperty('--cat-table-template-column-width', '1fr');
          }
          if (index !== 0) {
            cell.style.display = 'none'; /* 5 */
          }
        });
      }
    });
  }

  /**
   * End mobile view on screen sizes greater then 560px
   * 1. Remove event listener for click or keyboard event that toggles the moblie view modal
   * 2. Set the table column count to the number to table header cells
   * 3. If is the first table cell, the set the cell mobile view to false
   * 4. Show all the cells that are not in the first column
   */
  private endMobileView() {
    this._catTableRows.forEach((row: BlocksTableRow) => {
      if (row.isMobileView) {
        if (row.hasAttribute('isselffoldable')) {
          row.isSelfFoldable = true;
        }
      }
      const tableHeaderCells = this.querySelector<HTMLElement>('cat-table-header cat-table-row:last-child')?.querySelectorAll(
        'cat-table-header-cell'
      );
      const tableHeaderCellsCount = tableHeaderCells?.length;
      if (tableHeaderCellsCount !== undefined) {
        this.closest('cat-table-object')?.style.setProperty('--cat-table-column-count', tableHeaderCellsCount.toString()); /* 2 */
      }

      const rowTableCells = row.querySelectorAll<BlocksTableCell>('cat-table-cell, cat-table-header-cell');
      if (rowTableCells) {
        rowTableCells.forEach((cell: BlocksTableCell, index: number) => {
          if (row.isMobileView && index === 0) {
            cell.isMobileView = false; /* 3 */
          }
          if (index !== 0) {
            cell.style.display = 'contents'; /* 4 */
          }
        });
      }
    });
  }

  /**
   * Set mobile view
   * 1. Fire functions when the table object width is less than 560px
   */
  private setMobileView = () => {
    if (this._catIsMobileView) {
      /* 1 */
      if (this.mobileBreakPoint > this._catTableObjectWidth) {
        this.startMobileView();
      } else {
        this.endMobileView();
      }
    }
  };

  /**
   *
   * @param name - name of the emit event
   * @param value - value of the emit event
   */
  private eventEmit(name: string, value: {}) {
    return this.dispatch({
      eventName: name,
      detailObj: {
        value: value
      }
    });
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-table-object', {
      'cat-is-overflow-left': this.isStart === true,
      'cat-is-overflow-right': this.isEnd === true,
      'cat-is-bulk-action-active': this.isBulkActionActive === true,
      'cat-is-scrolling': this.isScrolling === true,
      'cat-c-table-cell-size-sm': this.tableCellSize === 'sm',
      'cat-c-table-cell-size-md': this.tableCellSize === 'md',
      'cat-is-odd-order-stripe': this.stripeOrder === 'odd',
      'cat-is-even-order-stripe': this.stripeOrder === 'even'
    });

    return html`
      <div part="base" class="${componentClassName}">
        ${this.slotNotEmpty('header') &&
        html` <div part="header-wrapper" class="cat-c-table-object__header"><slot part="part-header" name="header"></slot></div> `}
        ${this.tooltip === true
          ? html`
              <cat-tooltip
                part="tooltip"
                exportparts=${this.getExportParts('tooltip')}
                class="cat-c-table-object__tooltip"
                ?isActive=${this.showTooltip}
                style=${`inset-block-start: ${this.tooltipPosition?.[0]}px; left: ${this.tooltipPosition?.[1]}px;`}
                position=${ifDefined(this.tooltipAlign)}
              >
                ${this.tooltipText}
              </cat-tooltip>
            `
          : html`${nothing}`}
        <div part="body" class="cat-c-table-object__body">
          ${this.isBulkActionActive === true
            ? html`
                <div part="bulk-action" class="cat-c-table-object__bulk-action">
                  <div part="bulk-action-buttons" class="cat-c-table-object__bulk-action-buttons">
                    <span part="bulk-action-title" class="cat-c-table-object__bulk-action-title">
                      (${this.bulkActionCount}) ${this.bulkActionTitle ? this.bulkActionTitle : 'Actions'}:
                    </span>
                    ${this._catIsAnyBulkActions
                      ? html`
                          ${
                            this._catShowBulkActionSingle
                              ? html` <slot part="part-bulkActionSingle" name="bulkActionSingle"></slot> `
                              : html` <slot part="part-bulkAction" name="bulkAction"></slot> `
                          }
                          ${
                            this._catIsAnyBulkActionsMore &&
                            html`
                              <cat-contextual-menu
                                part="bulk-action-more"
                                exportparts=${this.getExportParts('contextual-menu')}
                                class="cat-c-table-object__bulk-action-more"
                                position="bottom-end"
                              >
                                <button part="more-trigger" slot="trigger" class="cat-c-table-object__bulk-action-more-trigger" aria-label="More">
                                  ${this.moreDropdownLabel}<cat-icon-delta-down
                                    part="icon-delta-down"
                                    exportparts=${this.getExportParts('delta-down', 'icon')}
                                    class="cat-c-table-object__bulk-action-more-trigger-icon"
                                    slot="after"
                                  ></cat-icon-delta-down>
                                </button>
                                ${this._catShowBulkActionSingleMore
                                  ? html` <slot part="part-bulkActionSingleMore" name="bulkActionSingleMore"></slot> `
                                  : html` <slot part="part-bulkActionMore" name="bulkActionMore"></slot> `}
                              </cat-contextual-menu>
                            `
                          }
                        </div>
                      `
                      : html`${nothing}`}
                    <cat-button
                      part="bulk-action-close"
                      exportparts=${this.getExportParts('button')}
                      class="cat-c-table-object__bulk-action-close"
                      @click=${this.toggleBulkActionActive}
                      ?hideText=${true}
                      variant="ghost"
                    >
                      Close<cat-icon-close part="icon-close" exportparts=${this.getExportParts('close', 'icon')} slot="after"></cat-icon-close>
                    </cat-button>
                  </div>
                </div>
              `
            : html`${nothing}`}
          <div part="body-inner" class="cat-c-table-object__body-inner" @scroll=${this.onScroll}>
            <span class="cat-u-is-vishidden" aria-live="polite">${this.draggedText}</span>

            <slot part="part-default"></slot>
          </div>
        </div>
        ${this.slotNotEmpty('footer') &&
        html` <div part="footer-wrapper" class="cat-c-table-object__footer"><slot part="part-footer" name="footer"></slot></div> `}
        ${this._catIsMobileView
          ? html`
              <!-- dismissible="false" - set intentionally, otherwise close icons will still appear in modal -->
              <!-- @ts-ignore -->
              <cat-modal
                exportparts=${this.getExportParts('modal')}
                part="modal"
                ?fullWidth=${true}
                ?backButton=${true}
                tabindex="0"
                dismissible="false"
              >
                <cat-heading
                  part="modal-heading"
                  exportparts=${this.getExportParts('heading')}
                  class="cat-c-modal__heading"
                  variant="title-sm"
                  slot="header"
                ></cat-heading>
                <div part="modal-body" class="cat-c-modal__body"></div>
              </cat-modal>
            `
          : html`${nothing}`}
      </div>
    `;
  }
}

if (customElements.get('cat-table-object') === undefined) {
  customElements.define('cat-table-object', BlocksTableObject);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-table-object': BlocksTableObject;
  }

  // internal event, so got added here
  interface GlobalEventHandlersEventMap {
    'bl-table-row-prop-change': CustomEvent<{ propName: string }>;
  }
}
